import { Container, Divider } from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import { Grid } from "@mui/material";
import axios from "axios";
import {
  ButtonGroupTop,
  ModalError,
  ModalSuccess,
  Page,
  PrimaryButton,
  SearchTextInput,
  SkeletonComponent,
  TableAction,
} from "components";
import { InnoTableV2 } from "inno-ui";
import { HeaderTitle } from "layouts";
import { getTotalPage } from "lib";
import { debounce } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router";
import { hardBaseUrl } from "services/urlConstant";
import { globalStyles } from "styles";
import { getErrors } from "utils";
import ModalFormParameterClientClassification from "./Components/ModalFormParameterClientClassification";

function ClientClassification() {
  const classes = globalStyles();
  const history = useHistory();
  const location = useLocation();
  const token = localStorage.getItem("token");
  const headers = {
    Authorization: `Bearer ${token}`,
  };
  const urlParams = new URLSearchParams(location.search);
  const paramsSearch = urlParams?.get("search");
  const paramsPage = urlParams?.get("page");
  const paramsSize = urlParams?.get("size");

  const [loadingPage, setLoadingPage] = useState(false);
  const [dataTable, setDataTable] = useState([]);
  const [queryParams, setQueryParams] = useState({
    page: Number(paramsPage) || 1,
    size: Number(paramsSize) || 10,
    search: paramsSearch || "",
  });
  const [tableTotalPage, setTableTotalPage] = useState(1);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalPreload, setModalPreload] = useState(null);

  const handleChangePageParams = (value, key) => {
    urlParams.set(key, value);
    history.replace({
      pathname: location.pathname,
      search: urlParams.toString(),
    });
  };
  const handleChangeQueryParams = (value, key) => {
    setQueryParams(currentState => ({
      ...currentState,
      ...(key !== "page" && { page: 1 }),
      [key]: value,
    }));
    handleChangePageParams(value, key);
    if (key !== "page") {
      handleChangePageParams(1, "page");
    }
  };

  const getDataTable = async () => {
    try {
      setLoadingPage(true);
      const res = await axios.get(
        `${hardBaseUrl}/publisher/classification/index`,
        {
          headers,
          params: queryParams,
        }
      );

      const { data, meta } = res?.data;
      setDataTable(data);
      const pageCount = getTotalPage(meta?.total, queryParams?.size || 1);
      setTableTotalPage(pageCount);
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingPage(false);
    }
  };
  const debounceDataTable = useCallback(
    debounce(() => {
      getDataTable();
    }, 500),
    [queryParams]
  );
  const handleDelete = async ({ client_classification_id }) => {
    try {
      setLoadingPage(true);
      await axios.delete(
        `${hardBaseUrl}/publisher/classification/delete/${client_classification_id}`,
        { headers }
      );
      ModalSuccess("Data Deleted Successfully").then(() => getDataTable());
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingPage(false);
    }
  };

  useEffect(() => {
    const isSearching = Boolean(queryParams?.search);
    if (isSearching) {
      debounceDataTable();
      return () => {
        debounceDataTable.cancel();
      };
    } else {
      getDataTable();
    }
  }, [queryParams, debounceDataTable]);

  return (
    <Page className={classes.root} title="Client Classification">
      <Container maxWidth={false}>
        <HeaderTitle title="Parameter" breadcrumbData={breadcrumbData} />
        <ButtonGroupTop />
        <Divider className={classes.flatDivider} />
        <Grid container justifyContent="space-between" my="16px">
          <Grid item>
            <SearchTextInput
              placeholder="Search"
              value={queryParams?.search}
              onChange={e =>
                handleChangeQueryParams(e?.target?.value, "search")
              }
            />
          </Grid>
          <Grid item>
            <PrimaryButton
              label="Add Client Classification"
              startIcon={<AddIcon />}
              onClick={() => {
                setModalVisible(true);
                setModalPreload(null);
              }}
            />
          </Grid>
        </Grid>
        {loadingPage ? (
          <SkeletonComponent variant="wave" />
        ) : (
          <InnoTableV2
            isLoading={false}
            columns={columnTable}
            items={dataTable}
            page={queryParams?.page}
            rowsPerPage={queryParams?.size}
            totalPage={tableTotalPage}
            handleChangePage={(_, page) =>
              handleChangeQueryParams(page, "page")
            }
            handleChangeRowsPerPage={e =>
              handleChangeQueryParams(e?.target?.value, "size")
            }
            onRequestSort={sortBy =>
              handleChangeQueryParams(
                `${sortBy} ${
                  queryParams.sort === `${sortBy} asc` ? "desc" : "asc"
                }`,
                "sort"
              )
            }
            isHaveAction
            renderAction={item => (
              <TableAction
                handleEdit={() => {
                  setModalVisible(true);
                  setModalPreload(item?.client_classification_id);
                }}
                deleteConfirmation
                deleteConfirmationKey={item?.classification_name}
                handleDelete={() => handleDelete(item)}
              />
            )}
          />
        )}
        {modalVisible && (
          <ModalFormParameterClientClassification
            open={modalVisible}
            onClose={() => setModalVisible(false)}
            preload={modalPreload}
            getDataTable={getDataTable}
          />
        )}
      </Container>
    </Page>
  );
}

const breadcrumbData = [
  {
    label: "Home",
    link: "/admin/dashboard",
  },
  {
    label: "Parameter",
    link: "/admin/parameter/client-classification",
  },
  {
    label: "Client Classification",
    active: true,
  },
];
const columnTable = [
  {
    name: "classification_name",
    title: "Name",
    sort_key: "classification_name",
    useSort: true,
  },
  {
    name: "description",
    title: "Description",
    sort_key: "description",
    useSort: true,
  },
];

export default ClientClassification;
